import { getId } from '@/store/utils'
import { XML_PARSER_ERROR } from '../constants'
import {
  buildIdMapByKey,
  buildTagIdByKey,
  mapNodeTree,
  mergeTagByTitle,
  normalizeTagTitle,
  walkSectionNodes,
} from '../modelUtils'
import type {
  XmlImportColorItem,
  XmlImportModel,
  XmlImportSectionItem,
  XmlImportTagItem,
} from '../types'
import { parseProjectColors } from './colors'
import { getChildElements, getOptionalChildText, parseXmlDocument } from './dom'
import type { ParsedXmlImportNodeItem } from './node'
import { type ParsedXmlImportSectionItem, parseSections } from './section'
import { parseProjectTags } from './tags'

const getProjectTitle = (root: Element): string =>
  getOptionalChildText(root, ['project_title']) || 'Imported project'

const createDraftJsRawContent = (value: string) => ({
  blocks: value.split(/\r?\n/).map((line) => ({
    key: getId(),
    text: line,
    type: 'unstyled',
    depth: 0,
    inlineStyleRanges: [],
    entityRanges: [],
    data: {},
  })),
  entityMap: {},
})

const toDraftJsRawContent = (value: string): string =>
  JSON.stringify(createDraftJsRawContent(value))

const validateRootStructure = (root: Element, treeElement: Element | null) => {
  const projectTitle = getOptionalChildText(root, ['project_title'])
  const sectionCount = treeElement
    ? getChildElements(treeElement, 'section').length
    : 0

  if (!projectTitle || !treeElement || sectionCount === 0) {
    throw new Error(XML_PARSER_ERROR)
  }
}

const collectTagsAndColors = (
  projectTags: XmlImportTagItem[],
  declaredColors: XmlImportColorItem[],
  sections: ParsedXmlImportSectionItem[]
): {
  tags: XmlImportTagItem[]
  colors: XmlImportColorItem[]
} => {
  const tagsByKey = new Map<string, XmlImportTagItem>()
  const colorsByHex = new Map<string, XmlImportColorItem>(
    declaredColors.map((color) => [color.hexCode, color])
  )

  const addColor = (hexCode?: string) => {
    if (hexCode && !colorsByHex.has(hexCode)) {
      colorsByHex.set(hexCode, { id: getId(), hexCode })
    }
  }

  const addTag = ({
    title,
    colorHex,
    id,
  }: {
    title: string
    colorHex?: string
    id?: string
  }) => {
    const tagKey = mergeTagByTitle({
      tagsByKey,
      title,
      ...(colorHex ? { colorHex } : {}),
      ...(id ? { id } : {}),
      createId: getId,
    })

    if (tagKey && colorHex) {
      addColor(colorHex)
    }
  }

  projectTags.forEach((tag) => {
    addTag({
      id: tag.id,
      title: tag.title,
      ...(tag.colorHex ? { colorHex: tag.colorHex } : {}),
    })
  })

  walkSectionNodes(sections, (node: ParsedXmlImportNodeItem) => {
    node.tags.forEach((tagTitle) => {
      addTag({
        title: tagTitle,
      })
    })

    addColor(node.colorHex)
    node.blocks.forEach((block) => {
      addColor(block.colorHex)
    })
  })

  return {
    tags: Array.from(tagsByKey.values()),
    colors: Array.from(colorsByHex.values()),
  }
}

const resolveSections = (
  sections: ParsedXmlImportSectionItem[],
  tagIdByKey: Map<string, string>,
  colorIdByHex: Map<string, string>
): XmlImportSectionItem[] =>
  sections.map((section) => ({
    id: section.id,
    title: section.title,
    nodes: mapNodeTree(
      section.nodes,
      (node: ParsedXmlImportNodeItem, children) => {
        const tagIds = Array.from(
          new Set(
            node.tags
              .map((title) => tagIdByKey.get(normalizeTagTitle(title)) || '')
              .filter(Boolean)
          )
        )
        const colorId = node.colorHex
          ? colorIdByHex.get(node.colorHex)
          : undefined

        return {
          id: node.id,
          title: node.title,
          seo: node.seo,
          tagIds,
          ...(colorId ? { colorId } : {}),
          blocks: node.blocks.map((block) => {
            const blockColorId = block.colorHex
              ? colorIdByHex.get(block.colorHex)
              : undefined

            return {
              id: block.id,
              title: block.title,
              ...(block.content
                ? { content: toDraftJsRawContent(block.content) }
                : {}),
              ...(block.wireframes?.length
                ? { wireframes: block.wireframes }
                : {}),
              ...(blockColorId ? { colorId: blockColorId } : {}),
            }
          }),
          children,
        }
      }
    ),
  }))

export const parseXmlImportModel = (payload: string): XmlImportModel => {
  const document = parseXmlDocument(payload)
  const root = document.documentElement
  const treeElement = getChildElements(root, 'tree')[0] || null

  validateRootStructure(root, treeElement)

  const parsedSections = parseSections(treeElement)
  const { tags, colors } = collectTagsAndColors(
    parseProjectTags(root),
    parseProjectColors(root),
    parsedSections
  )
  const sections = resolveSections(
    parsedSections,
    buildTagIdByKey(tags),
    buildIdMapByKey({
      items: colors,
      getKey: (color) => color.hexCode,
      getId: (color) => color.id,
    })
  )

  return {
    projectTitle: getProjectTitle(root),
    colors,
    tags,
    sections,
  }
}
